import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Timer, ShieldCheck, Sparkles, FileText, Star, MessageSquare, Send, Wrench, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { STATUS_CONFIG } from '@/components/workorder/constants';
import { ClientVehicleInfo } from '@/components/workorder/ClientVehicleInfo';
import { PartsSection } from '@/components/workorder/PartsSection';
import { SuppliersOrderSection } from '@/components/workorder/SuppliersOrderSection';
import { LabourSection } from '@/components/workorder/LabourSection';
import { AiPartsRecommendation } from '@/components/workorder/AiPartsRecommendation';
import { AtelierPartsOrder } from '@/components/workorder/AtelierPartsOrder';
import { ChecklistDescription } from '@/components/workorder/ChecklistDescription';
import { ClientActionsSection } from '@/components/workorder/ClientActionsSection';
import { InvoiceSection } from '@/components/workorder/InvoiceSection';
import { LiquidesSection } from '@/components/workorder/LiquidesSection';
import { ChatSection } from '@/components/workorder/ChatSection';
import { HistorySection } from '@/components/workorder/HistorySection';
import { VehiclePickupSection } from '@/components/workorder/VehiclePickupSection';
import { PreparationOccasionSection } from '@/components/workorder/PreparationOccasionSection';
import { PreparationLivraisonSection } from '@/components/workorder/PreparationLivraisonSection';
import { OrderSlipsSection } from '@/components/workorder/OrderSlipsSection';
import { AttachmentsSection } from '@/components/workorder/AttachmentsSection';
import WoDirectMessage from '@/components/workorder/WoDirectMessage';
import { Card } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { CheckInSummary } from '@/components/workorder/CheckInSummary';
import { ReadySummary } from '@/components/workorder/atelier/ReadySummary';
import { ConfirmBilledButton } from '@/components/workorder/ConfirmBilledButton';
import { WorkshopSheet, Fold } from '@/components/workorder/sheet/WorkshopSheet';

// Error Boundary for catching render crashes
class WorkOrderErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center space-y-4" data-testid="workorder-error">
          <p className="text-red-600 font-medium">Erreur d'affichage de la fiche de travail</p>
          <p className="text-sm text-slate-500">{this.state.error?.message || 'Erreur inconnue'}</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-slate-100 rounded-lg text-sm hover:bg-slate-200"
          >
            Retour
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const WorkOrderPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const { woId } = useParams();
  const navigate = useNavigate();
  const role = auth?.admin?.role || 'admin';
  const isAtelier = role === 'atelier';
  const canEditPricing = !isAtelier;

  // Core work order state
  const [workOrder, setWorkOrder] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [client, setClient] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeSummary, setTimeSummary] = useState(null);

  // Checklist & description state
  const [checklist, setChecklist] = useState({});
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [sinistreNumbers, setSinistreNumbers] = useState(['', '', '']);

  // Parts orders (reception)
  const [partsOrders, setPartsOrders] = useState([]);

  // Invoice state
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const [approvers, setApprovers] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [nextServiceDate, setNextServiceDate] = useState('');

  // Parts (inventory) state
  const [woParts, setWoParts] = useState([]);
  const [totalPartsCost, setTotalPartsCost] = useState(0);
  const [predefinedCategories, setPredefinedCategories] = useState([]);

  // Labour state
  const [timeEntries, setTimeEntries] = useState(null);
  const [allocatedHours, setAllocatedHours] = useState('');
  const [recherchePanne, setRecherchePanne] = useState(null);
  const [diagnosticObd, setDiagnosticObd] = useState(null);
  const [servicePlus, setServicePlus] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Atelier parts orders
  const [atelierPartsOrders, setAtelierPartsOrders] = useState([]);
  const [deliveryBulletins, setDeliveryBulletins] = useState([]);

  // Satisfaction rating
  const [satisfaction, setSatisfaction] = useState(null);

  // Additional work requests
  const [additionalRequests, setAdditionalRequests] = useState([]);

  // Liquides
  const [liquides, setLiquides] = useState({
    huile_moteur: '', refroidissement: '', lave_glace: '', liquide_frein: '', direction_assistee: '', huile_transmission: '',
  });

  // --- Data Loading ---
  useEffect(() => {
    loadWorkOrder();
    loadAdditionalRequests();
    loadApprovers();
    loadWoParts();
    loadPredefinedParts();
    loadTimeEntries();
    loadEmployees();
    loadSatisfaction();
  }, [woId]);

  useEffect(() => { loadAtelierPartsOrders(); }, [woId]);

  useEffect(() => {
    if (workOrder?.liquides) setLiquides(prev => ({ ...prev, ...workOrder.liquides }));
  }, [workOrder]);

  // WebSocket for work order update alerts (non-chat)
  useEffect(() => {
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/notifications`;
    let ws;
    let reconnectTimer;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'work_order_updated' && data.work_order_id === woId) {
            if (data.sender_role !== role) {
              playAlertSound();
              toast.info(`Fiche modifiee par ${data.sender_name}`, {
                description: `Champs: ${data.changed_fields?.join(', ') || 'mise a jour'}`,
                duration: 8000,
                style: { background: '#eff6ff', border: '2px solid #3b82f6' },
              });
              loadWorkOrder();
              loadTimeEntries();
            }
          }
          // Client signed the work order
          if (data.type === 'work_order_signed' && data.work_order_id === woId) {
            playAlertSound();
            toast.success(data.message || 'Fiche signée par le client', {
              duration: 10000,
              style: { background: '#ecfdf5', border: '2px solid #10b981' },
            });
            loadWorkOrder();
          }
          // Client responded to additional work request
          if (data.type === 'additional_work_response' && data.work_order_id === woId) {
            playAlertSound();
            toast.info(data.message || 'Réponse du client', {
              duration: 10000,
              style: { background: data.status === 'approved' ? '#ecfdf5' : '#fef2f2', border: `2px solid ${data.status === 'approved' ? '#10b981' : '#ef4444'}` },
            });
            loadWorkOrder();
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
    };

    connect();
    return () => { clearTimeout(reconnectTimer); ws?.close(); };
  }, [woId, role]);

  const playAlertSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch { /* ignore */ }
  };

  const loadWorkOrder = async () => {
    try {
      const woRes = await axios.get(`${API}/work-orders/${woId}`);
      const wo = woRes.data;
      setWorkOrder(wo);
      setChecklist(wo.checklist || {});
      setDescription(wo.description || '');
      setInvoiceNumber(wo.invoice_number || '');
      setInvoiceAmount(wo.invoice_amount ? String(wo.invoice_amount) : '');
      setApprovedBy(wo.approved_by || '');
      const sns = wo.sinistre_numbers || (wo.sinistre_number ? [wo.sinistre_number, '', ''] : ['', '', '']);
      setSinistreNumbers(sns);
      setPartsOrders(wo.parts_orders || []);

      const [apptRes, clientRes, vehicleRes] = await Promise.all([
        axios.get(`${API}/appointments/${wo.appointment_id}`).catch(() => null),
        axios.get(`${API}/clients/${wo.client_id}`).catch(() => null),
        axios.get(`${API}/vehicles/${wo.vehicle_id}`).catch(() => null),
      ]);
      if (apptRes) setAppointment(apptRes.data);
      if (clientRes) setClient(clientRes.data);
      if (vehicleRes) setVehicle(vehicleRes.data);

      try {
        const timeRes = await axios.get(`${API}/work-time/summary/${woId}`);
        setTimeSummary(timeRes.data);
      } catch { /* ignore */ }

      if (wo.next_service_date) {
        setNextServiceDate(wo.next_service_date);
      } else {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 1);
        setNextServiceDate(d.toISOString().split('T')[0]);
      }
    } catch {
      toast.error('Fiche de travail introuvable');
      navigate('/admin/agenda');
    } finally { setLoading(false); }
  };

  const loadTimeEntries = async () => {
    try {
      const res = await axios.get(`${API}/work-orders/${woId}/time-entries`);
      setTimeEntries(res.data);
      if (res.data.allocated_hours != null) setAllocatedHours(String(res.data.allocated_hours));
      if (res.data.recherche_panne) setRecherchePanne(res.data.recherche_panne);
      if (res.data.diagnostic_obd) setDiagnosticObd(res.data.diagnostic_obd);
      if (res.data.service_plus) setServicePlus(res.data.service_plus);
    } catch { /* ignore */ }
  };

  const loadEmployees = async () => {
    try { const res = await axios.get(`${API}/employees`); setEmployees(res.data.filter(e => e.is_active)); } catch { /* ignore */ }
  };

  const loadWoParts = async () => {
    try { const res = await axios.get(`${API}/work-orders/${woId}/parts`); setWoParts(res.data.parts); setTotalPartsCost(res.data.total_parts_cost); } catch { /* ignore */ }
  };

  const loadPredefinedParts = async () => {
    try { const res = await axios.get(`${API}/work-orders/predefined-parts`); setPredefinedCategories(res.data); } catch { /* ignore */ }
  };

  const loadAdditionalRequests = async () => {
    try { const res = await axios.get(`${API}/additional-work-requests`, { params: { work_order_id: woId } }); setAdditionalRequests(res.data); } catch { /* ignore */ }
  };

  const loadApprovers = async () => {
    try { const res = await axios.get(`${API}/work-orders/approvers`); setApprovers(res.data); } catch { /* ignore */ }
  };

  const loadSatisfaction = async () => {
    try { const res = await axios.get(`${API}/satisfaction/work-order/${woId}`); setSatisfaction(res.data); } catch { /* ignore */ }
  };

  const sendSatisfactionWhatsApp = async () => {
    if (!satisfaction?.survey_id) return;
    try {
      const res = await axios.post(`${API}/satisfaction/admin/send-whatsapp/${satisfaction.survey_id}`);
      if (res.data.success) { toast.success('WhatsApp de satisfaction envoye'); loadSatisfaction(); }
      else toast.error(res.data.error || 'Erreur');
    } catch { toast.error('Erreur envoi WhatsApp'); }
  };


  const loadAtelierPartsOrders = async () => {
    try {
      const res = await axios.get(`${API}/atelier/work-orders/${woId}/atelier-parts-orders`);
      setAtelierPartsOrders(res.data.atelier_parts_orders || []);
      setDeliveryBulletins(res.data.delivery_bulletins || []);
    } catch { /* ignore */ }
  };

  // --- Computed values ---
  const labourTimeCost = timeEntries ? timeEntries.total_cost_ht : 0;
  const recherchePanneCost = recherchePanne?.active ? (recherchePanne.cost_ht || 0) : 0;
  const diagnosticObdCost = diagnosticObd?.active ? (diagnosticObd.cost_ht || 0) : 0;
  const normalizedSP = Array.isArray(servicePlus) ? servicePlus : (servicePlus && typeof servicePlus === 'object' ? Object.keys(servicePlus).filter(k => servicePlus[k]) : []);
  const [spTariffs, setSpTariffs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/settings/service-plus-tariffs`).then(r => { if (r.data.tariffs?.length) setSpTariffs(r.data.tariffs); }).catch(() => {});
  }, []);
  const servicePlusCost = normalizedSP.reduce((sum, id) => {
    const item = spTariffs.find(s => s.id === id);
    return sum + (item ? item.price : 0);
  }, 0);
  const totalLabourHT = labourTimeCost + recherchePanneCost + diagnosticObdCost + servicePlusCost;
  const totalHT = totalPartsCost + totalLabourHT;
  const tvaRate = 8.1;
  const totalTVA = Math.round(totalHT * tvaRate) / 100;
  const totalTTC = Math.round((totalHT + totalTVA) * 100) / 100;

  useEffect(() => {
    if (workOrder && workOrder.status !== 'completed' && totalTTC > 0) {
      setInvoiceAmount(String(totalTTC));
    }
  }, [totalPartsCost, timeEntries, workOrder]);

  // --- Actions ---
  const saveChecklist = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/work-orders/${woId}`, { checklist, description, sinistre_numbers: sinistreNumbers, parts_orders: partsOrders });
      toast.success('Fiche enregistrée');
      loadWorkOrder();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const saveSinistreNumber = async (idx, val) => {
    const updated = [...sinistreNumbers];
    updated[idx] = val;
    setSinistreNumbers(updated);
    try { await axios.put(`${API}/work-orders/${woId}`, { sinistre_numbers: updated }); } catch { /* auto-save */ }
  };

  const updateLiquide = async (key, value) => {
    const updated = { ...liquides, [key]: value };
    setLiquides(updated);
    try { await axios.put(`${API}/work-orders/${woId}`, { liquides: updated }); } catch { /* auto-save */ }
  };

  const downloadPdf = async () => {
    try {
      const { downloadPdf: safePdf } = await import('@/utils/api');
      await safePdf(`${API}/work-orders/${woId}/pdf`, `fiche_travail_${woId.substring(0, 8)}.pdf`);
      toast.success('PDF téléchargé');
    } catch { toast.error('Erreur lors du telechargement du PDF'); }
  };

  const downloadClientPdf = async () => {
    try {
      const { downloadPdf: safePdf } = await import('@/utils/api');
      await safePdf(`${API}/work-orders/${woId}/pdf-client`, `fiche_entretien_${woId.substring(0, 8)}.pdf`);
      toast.success('Fiche entretien telechargee');
    } catch { toast.error('Erreur lors du telechargement'); }
  };

  const generateInvoice = async () => {
    try {
      const res = await axios.post(`${API}/billing/generate-from-wo/${woId}`);
      toast.success(`Facture ${res.data.number} générée : ${res.data.total.toFixed(2)} CHF`);
      loadWorkOrder();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la génération'); }
  };

  const completeWorkOrder = async () => {
    const internal = workOrder?.billing_type === 'interne';
    if (!invoiceNumber && !internal) { toast.error('Numero de facture obligatoire'); return; }
    if (!approvedBy) { toast.error('Veuillez sélectionner un approbateur'); return; }
    const approver = approvers.find(a => a.id === approvedBy);
    const approverName = approver ? `${approver.first_name} ${approver.last_name}` : '';
    setCompleting(true);
    try {
      await axios.put(`${API}/work-orders/${woId}`, {
        status: 'completed', invoice_number: internal ? (invoiceNumber || 'INTERNE').toUpperCase() : invoiceNumber.toUpperCase(),
        invoice_amount: parseFloat(invoiceAmount) || 0, approved_by: approvedBy,
        approved_by_name: approverName, next_service_date: nextServiceDate,
      });
      toast.success('Fiche clôturée avec succès');
      loadWorkOrder();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la clôture'); }
    finally { setCompleting(false); }
  };

  // --- Render ---
  if (loading) return <AdminLayout auth={auth} onLogout={onLogout}><div className="p-8 text-center text-slate-500">{t('common.loading')}</div></AdminLayout>;
  if (!workOrder) return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-8 text-center space-y-4" data-testid="workorder-not-found">
        <p className="text-slate-500">Fiche de travail introuvable ou supprimee.</p>
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="go-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
      </div>
    </AdminLayout>
  );

  const statusConf = STATUS_CONFIG[workOrder.status] || STATUS_CONFIG.open;

  const isLivraison = workOrder.source === 'preparation_livraison';

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6 max-w-4xl">
        {/* Header */}
        <div className={`flex items-center justify-between ${isLivraison ? 'border-l-4 border-l-orange-500 pl-4' : ''}`}>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-to-workorders">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className={`text-4xl font-heading font-bold ${isLivraison ? 'text-orange-700' : 'text-slate-900'}`} data-testid="workorder-title">
                {isLivraison ? 'Fiche livraison' : 'Fiche de travail'}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Ref: {woId.substring(0, 8).toUpperCase()}
                {isLivraison && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">Livraison</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusConf.color}`} data-testid="workorder-status">{statusConf.label}</span>
            {workOrder.billing_type === 'devis' && (
              <span className="text-xs px-3 py-1 rounded-full font-bold bg-amber-100 text-amber-700 border border-amber-300" data-testid="billing-type-badge">DEVIS</span>
            )}
            {workOrder.billing_type === 'reparation' && (
              <span className="text-xs px-3 py-1 rounded-full font-medium bg-blue-100 text-blue-700" data-testid="billing-type-badge">Réparation</span>
            )}
            {workOrder.parts?.some(p => !p.unit_price || p.unit_price <= 0) && (
              <span className="text-xs px-3 py-1 rounded-full font-medium bg-amber-100 text-amber-700 animate-pulse" data-testid="prix-a-confirmer-badge">
                Prix a confirmer
              </span>
            )}
            {partsOrders && partsOrders.length > 0 && (() => {
              const notOrdered = partsOrders.filter(o => o.status === 'pas_commande').length;
              const pending = partsOrders.filter(o => o.status === 'en_cours').length;
              const received = partsOrders.filter(o => o.status === 'recu').length;
              if (notOrdered > 0) return <span className="text-xs px-3 py-1 rounded-full font-bold bg-red-100 text-red-700 border border-red-300 animate-pulse" data-testid="parts-not-ordered-badge">{notOrdered} piece(s) pas commandee(s)</span>;
              if (pending > 0) return <span className="text-xs px-3 py-1 rounded-full font-bold bg-amber-100 text-amber-700 border border-amber-300" data-testid="parts-pending-badge">{pending} commande(s) en cours</span>;
              if (received === partsOrders.length) return <span className="text-xs px-3 py-1 rounded-full font-medium bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid="parts-all-received-badge">Pieces recues</span>;
              return null;
            })()}
            <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-pdf-btn"><Download className="mr-1 w-4 h-4" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={downloadClientPdf} data-testid="download-client-pdf-btn" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"><FileText className="mr-1 w-4 h-4" /> Fiche client</Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/workshop/${woId}`)} data-testid="timetracking-btn"><Timer className="mr-1 w-4 h-4" /> Pointeuse</Button>
          </div>
        </div>

        {/* Billing type toggle */}
        {canEditPricing && (
          <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200" data-testid="billing-type-toggle">
            <span className="text-xs font-semibold text-slate-500 uppercase">Type :</span>
            <button
              onClick={async () => {
                try {
                  await axios.put(`${API}/work-orders/${woId}`, { billing_type: 'reparation' });
                  loadWorkOrder();
                } catch { toast.error('Erreur'); }
              }}
              className={`py-1.5 px-4 rounded-lg text-sm font-bold border-2 transition-all ${
                workOrder.billing_type !== 'devis'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'
              }`}
              data-testid="toggle-reparation"
            >
              Réparation
            </button>
            <button
              onClick={async () => {
                try {
                  await axios.put(`${API}/work-orders/${woId}`, { billing_type: 'devis' });
                  loadWorkOrder();
                } catch { toast.error('Erreur'); }
              }}
              className={`py-1.5 px-4 rounded-lg text-sm font-bold border-2 transition-all ${
                workOrder.billing_type === 'devis'
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-amber-300'
              }`}
              data-testid="toggle-devis"
            >
              Devis
            </button>
          </div>
        )}

        {/* PRET ATELIER banner */}
        {workOrder.atelier_ready && workOrder.status !== 'completed' && canEditPricing && (
          <div className="flex items-center justify-between p-4 bg-emerald-50 border-2 border-emerald-400 rounded-xl animate-pulse" data-testid="atelier-ready-banner">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="font-bold text-emerald-800 text-lg">VEHICULE PRET - Retour atelier</p>
                <p className="text-sm text-emerald-600">L'atelier a termine le travail sur cette fiche</p>
              </div>
            </div>
            <Button
              onClick={async () => {
                try {
                  await axios.put(`${API}/work-orders/${woId}`, { atelier_ready: false });
                  toast.success('Fiche reprise');
                  loadWorkOrder();
                } catch { toast.error('Erreur'); }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6"
              data-testid="reprendre-fiche-btn"
            >
              Reprendre la fiche
            </Button>
          </div>
        )}

        {/* Rouvrir fiche completed */}
        {workOrder.status === 'completed' && canEditPricing && (
          <div className="flex items-center justify-between p-4 bg-slate-100 border border-slate-300 rounded-xl dark:bg-slate-800 dark:border-slate-700" data-testid="reopen-banner">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-slate-500" />
              <p className="font-medium text-slate-600 dark:text-slate-300">Fiche terminee et facturee</p>
            </div>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await axios.put(`${API}/work-orders/${woId}`, { status: 'in_progress' });
                  toast.success('Fiche rouverte');
                  loadWorkOrder();
                } catch { toast.error('Erreur'); }
              }}
              data-testid="reopen-fiche-btn"
            >
              Rouvrir la fiche
            </Button>
          </div>
        )}

        {/* Satisfaction client */}
        {(workOrder.status === 'completed' || workOrder.status === 'invoiced') && (satisfaction || workOrder.satisfaction_rating) && (
          <div className="p-4 rounded-xl border bg-white dark:bg-slate-800 dark:border-slate-700" data-testid="satisfaction-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-amber-500" />
                <span className="font-medium text-sm text-slate-700 dark:text-slate-200">Satisfaction client</span>
              </div>
              {(satisfaction?.has_rating || workOrder.satisfaction_rating) ? (
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`w-5 h-5 ${i <= (satisfaction?.rating || workOrder.satisfaction_rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-200 dark:text-slate-600'}`} />
                    ))}
                  </div>
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{satisfaction?.rating || workOrder.satisfaction_rating}/5</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">
                    {satisfaction?.whatsapp_sent ? 'WhatsApp envoye — en attente' : 'Pas encore envoye'}
                  </span>
                  {!satisfaction?.whatsapp_sent && (
                    <Button size="sm" variant="outline" onClick={sendSatisfactionWhatsApp} className="h-7 text-xs gap-1" data-testid="send-satisfaction-wa">
                      <Send className="w-3 h-3" /> Envoyer WhatsApp
                    </Button>
                  )}
                </div>
              )}
            </div>
            {((satisfaction?.has_rating && satisfaction.comment) || workOrder.satisfaction_comment) && (
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 italic pl-8">"{satisfaction?.comment || workOrder.satisfaction_comment}"</p>
            )}
          </div>
        )}

        {/* Hide Client/Vehicle cards for VO work orders (info is in Fiche Occasion/Livraison) */}
        {workOrder.source !== 'preparation_vo' && workOrder.source !== 'preparation_livraison' && workOrder.wo_type !== 'parc_occasion' && (
          <ClientVehicleInfo client={client} vehicle={vehicle} appointment={appointment} timeSummary={timeSummary} isAtelier={isAtelier} />
        )}

        {/* Reprise vehicule — date et heure */}
        <VehiclePickupSection woId={woId} workOrder={workOrder} appointment={appointment} onUpdated={loadWorkOrder} />

        {/* OVERTIME ALERT BANNER — visible to all, especially atelier */}
        {allocatedHours && timeEntries && timeEntries.total_hours > parseFloat(allocatedHours) && workOrder.status !== 'completed' && (
          <div className="border-2 border-red-400 bg-red-50 rounded-xl p-4 flex items-center gap-4 animate-pulse" data-testid="overtime-banner">
            <div className="w-12 h-12 rounded-full bg-red-500 text-white flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <p className="text-base font-bold text-red-800">TEMPS DEPASSE</p>
              <p className="text-sm text-red-700">
                Temps alloue: <span className="font-bold">{allocatedHours}h</span> — Temps realise: <span className="font-bold">{timeEntries.total_hours}h</span>
                <span className="ml-2 text-red-900 font-bold bg-red-200 px-2 py-0.5 rounded-full text-xs">
                  +{(timeEntries.total_hours - parseFloat(allocatedHours)).toFixed(1)}h de depassement
                </span>
              </p>
            </div>
          </div>
        )}

        {/* Simplified preparation checklist for Parc Occasion work orders */}
        {(workOrder.source === 'preparation_vo' || workOrder.wo_type === 'parc_occasion') && (
          <PreparationOccasionSection woId={woId} workOrder={workOrder} canEdit={true} />
        )}

        {/* Preparation Livraison section */}
        {workOrder.source === 'preparation_livraison' && (
          <PreparationLivraisonSection woId={woId} workOrder={workOrder} canEdit={true} />
        )}

        {workOrder.source !== 'preparation_vo' && workOrder.source !== 'preparation_livraison' && workOrder.wo_type !== 'parc_occasion' && (
          <WorkshopSheet woId={woId} workOrder={workOrder} appointment={appointment} onChanged={loadWorkOrder}
            photos={<AttachmentsSection woId={woId} workOrder={workOrder} isAtelier={isAtelier} />} />
        )}

        <Fold title="Pièces et commandes" testid="wo-fold-parts">
        <div className="flex items-center gap-2 mb-2">
          <AiPartsRecommendation workOrder={workOrder} />
        </div>
        <PartsSection woId={woId} workOrder={workOrder} canEditPricing={canEditPricing} woParts={woParts} totalPartsCost={totalPartsCost} predefinedCategories={predefinedCategories} onPartsChanged={loadWoParts} />

        <OrderSlipsSection woId={woId} workOrder={workOrder} uploadedBy={auth?.admin?.name || 'Admin'} onImported={loadWoParts} />

        {!isAtelier && (
          <SuppliersOrderSection woId={woId} partsOrders={partsOrders} setPartsOrders={setPartsOrders} />
        )}

        {isAtelier && (
          <SuppliersOrderSection woId={woId} partsOrders={partsOrders} setPartsOrders={setPartsOrders} />
        )}

        {isAtelier && (
          <AtelierPartsOrder
            woId={woId} workOrder={workOrder} employees={employees}
            atelierPartsOrders={atelierPartsOrders} deliveryBulletins={deliveryBulletins}
            onOrdersChanged={loadAtelierPartsOrders} onVehicleReady={loadWorkOrder}
          />
        )}
        </Fold>

        <Fold title="Temps et main-d'œuvre" testid="wo-fold-labour">
        <LabourSection
          woId={woId} workOrder={workOrder} canEditPricing={canEditPricing}
          timeEntries={timeEntries} allocatedHours={allocatedHours} setAllocatedHours={setAllocatedHours}
          recherchePanne={recherchePanne} setRecherchePanne={setRecherchePanne}
          diagnosticObd={diagnosticObd} setDiagnosticObd={setDiagnosticObd}
          servicePlus={servicePlus} setServicePlus={setServicePlus}
          employees={employees} onTimeEntriesChanged={loadTimeEntries}
        />
        </Fold>

        {workOrder.ready_summary && <ReadySummary s={workOrder.ready_summary} by={workOrder.atelier_ready_by} at={workOrder.atelier_ready_at} />}
        <CheckInSummary checkin={workOrder.checkin} checkout={workOrder.checkout} woId={woId} />
        <Fold title="Check-list et ancienne description" testid="wo-fold-reception">
        <ChecklistDescription
          woId={woId} workOrder={workOrder} isAtelier={isAtelier}
          checklist={checklist} setChecklist={setChecklist}
          description={description} setDescription={setDescription}
          sinistreNumbers={sinistreNumbers} onSaveSinistreNumber={saveSinistreNumber}
          onSaveChecklist={saveChecklist} saving={saving}
        />
        {(workOrder.source === 'preparation_vo' || workOrder.source === 'preparation_livraison' || workOrder.wo_type === 'parc_occasion') && (
          <AttachmentsSection woId={woId} workOrder={workOrder} isAtelier={isAtelier} />
        )}
        </Fold>

        <Fold title="Client, accord et facturation" testid="wo-fold-client">
        {/* Communication directe avec le client */}
        {workOrder.client_id && (
          <WoDirectMessage clientId={workOrder.client_id} clientName={workOrder.client_name} woReference={workOrder.wo_number || woId?.slice(0,8)} woId={woId} />
        )}

        {/* Notes atelier (visible côté admin) */}
        {workOrder.atelier_notes && workOrder.atelier_notes.length > 0 && (
          <Card className="p-4 rounded-xl space-y-2" data-testid="atelier-notes-section">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-amber-600" /> Notes mecanicien ({workOrder.atelier_notes.length})
            </h3>
            <div className="space-y-2">
              {workOrder.atelier_notes.map((note, i) => (
                <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-sm text-slate-800">{note.content || note.text}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {note.employee_name || note.mechanic_name || ''} — {note.created_at ? new Date(note.created_at).toLocaleString('fr-CH') : ''}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {canEditPricing && (
          <InvoiceSection
            workOrder={workOrder} navigate={navigate}
            invoiceNumber={invoiceNumber} setInvoiceNumber={setInvoiceNumber}
            invoiceAmount={invoiceAmount} setInvoiceAmount={setInvoiceAmount}
            approvedBy={approvedBy} setApprovedBy={setApprovedBy} approvers={approvers}
            nextServiceDate={nextServiceDate} setNextServiceDate={setNextServiceDate}
            totalPartsCost={totalPartsCost} totalLabourHT={totalLabourHT}
            totalHT={totalHT} totalTTC={totalTTC} tvaRate={tvaRate}
            onComplete={completeWorkOrder} completing={completing}
            onGenerateInvoice={generateInvoice}
          />
        )}

        <ClientActionsSection
          woId={woId} workOrder={workOrder} client={client} isAtelier={isAtelier} navigate={navigate}
          additionalRequests={additionalRequests} onAdditionalRequestsChanged={loadAdditionalRequests}
          onLoadWorkOrder={loadWorkOrder}
        />

        {/* Quality checks status (admin view - read only) */}
        {(workOrder.quality_tested_by || workOrder.quality_cleaned_by) && (
          <Card className="p-4 rounded-xl space-y-2" data-testid="quality-checks-admin">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Controle qualite atelier
            </h3>
            <div className="space-y-1.5">
              {workOrder.quality_tested_by && (
                <div className="flex items-center gap-2 text-sm bg-emerald-50 rounded-lg px-3 py-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  <span className="text-emerald-700">Teste par <strong>{workOrder.quality_tested_by}</strong></span>
                  {workOrder.quality_tested_at && <span className="text-xs text-emerald-500 ml-auto">{new Date(workOrder.quality_tested_at).toLocaleString('fr-CH')}</span>}
                </div>
              )}
              {workOrder.quality_cleaned_by && (
                <div className="flex items-center gap-2 text-sm bg-violet-50 rounded-lg px-3 py-2">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  <span className="text-violet-700">Nettoye par <strong>{workOrder.quality_cleaned_by}</strong></span>
                  {workOrder.quality_cleaned_at && <span className="text-xs text-violet-500 ml-auto">{new Date(workOrder.quality_cleaned_at).toLocaleString('fr-CH')}</span>}
                </div>
              )}
            </div>
          </Card>
        )}
        </Fold>

        <Fold title="Niveaux, historique et messages internes" testid="wo-fold-misc">
        <LiquidesSection workOrder={workOrder} liquides={liquides} onUpdateLiquide={updateLiquide} />

        <HistorySection workOrder={workOrder} />

        <ChatSection woId={woId} role={role} auth={auth} />
        </Fold>

        {!isAtelier && (
          <div className="rounded-xl border-2 border-[#FACC15] bg-[#0E1116] p-4 flex flex-wrap items-center gap-3" data-testid="wo-end-actions">
            <p className="text-white font-bold flex-1 min-w-[200px]">Fin de fiche : facturer ici ou imprimer le détail pour un autre programme</p>
            {workOrder.billing_type === 'interne'
              ? <span className="text-[#FACC15] font-semibold text-sm" data-testid="wo-end-internal-label">Tâche interne – non facturée</span>
              : workOrder.invoice_id
              ? <Button variant="outline" onClick={() => navigate('/admin/billing')} data-testid="wo-end-view-invoice-btn">Voir la facture {workOrder.invoice_number}</Button>
              : <Button className="bg-blue-600 hover:bg-blue-700" onClick={generateInvoice} data-testid="wo-end-invoice-btn">Facturer</Button>}
            <Button className="bg-[#FACC15] text-[#0E1116] hover:bg-yellow-300" onClick={() => window.open(`/admin/work-orders/${woId}/detail`, '_blank')} data-testid="wo-end-print-detail-btn">Imprimer le détail</Button>
            {workOrder.billing_type !== 'interne' && <ConfirmBilledButton wo={workOrder} onDone={loadWorkOrder} />}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const WorkOrderPageWrapped = (props) => (
  <WorkOrderErrorBoundary>
    <WorkOrderPage {...props} />
  </WorkOrderErrorBoundary>
);

export default WorkOrderPageWrapped;
